import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { ConfirmProvider } from '@/components/common/ConfirmProvider';
import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { RouterProvider } from 'react-router-dom';
import { Loadable } from '@/components/common/PageLoading';
import { queryClient } from '@/app/queryClient';
import { router } from '@/routes';
import { createAppTheme } from '@/theme/theme';
import { brandingAsset, useBranding } from '@/theme/useBranding';
import { useUiStore } from '@/store/uiStore';

/** The MUI theme from the persisted look and colour mode, in the organisation's colours where it has set them. */
function Themed() {
  const colorMode = useUiStore((s) => s.colorMode);
  const themeName = useUiStore((s) => s.themeName);
  const branding = useBranding();
  const theme = useMemo(() => createAppTheme(colorMode, themeName, { primary: branding?.primaryColour, secondary: branding?.secondaryColour }),
    [colorMode, themeName, branding?.primaryColour, branding?.secondaryColour]);
  // The organisation's favicon replaces the product's in the browser tab (docs §273, URS ADM-01).
  const favicon = brandingAsset(branding?.faviconUrl);
  useEffect(() => {
    if (!favicon) return;
    const links = [...document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]')];
    const before = links.map((l) => l.href);
    links.forEach((l) => { l.href = favicon; });
    return () => links.forEach((l, i) => { l.href = before[i]; });
  }, [favicon]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ConfirmProvider>
        <Loadable><RouterProvider router={router} /></Loadable>
      </ConfirmProvider>
    </ThemeProvider>
  );
}

/**
 * Root application component. Wires up React Query, the MUI theme (persisted look and colour mode,
 * the organisation's branding) and the router.
 */
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Themed />
    </QueryClientProvider>
  );
}
